/**
 * Il contenuto di un carrello, aperto dal nome sulla tessera dello scarico.
 *
 * Sulla schermata di scarico un carrello è una riga di codici — `K1 · 2/4 ·
 * C21 C33` — e va bene finché serve a sapere se c'è ancora posto. Non basta
 * quando il carrello sta per partire: chi lo spinge deve poter controllare che
 * sopra ci sia la merce che crede, e due sigle non glielo dicono.
 *
 * **La forma è quella del carrello, non quella di un elenco.** Ogni cassetta è
 * una scheda larga con a sinistra le due cose che si leggono da lontano — il
 * codice e quanti capi — e a destra tutto ciò che si legge da vicino. I posti
 * ancora liberi sono tessere anche loro: «tre su quattro» è una sottrazione da
 * fare in testa stando davanti a un carrello con le mani occupate, mentre un
 * riquadro vuoto si conta guardando, come si contano le cassette vere.
 *
 * **Non chiede una rotta sua.** La vista dello scarico porta già
 * `carrelli: Record<codice, cassette>`: questa pagina rilegge quella e ne
 * mostra una casella. Un endpoint dedicato sarebbe una seconda lettura degli
 * stessi dati da tenere d'accordo con la prima, e il `useFlusso` che si porta
 * dietro rilegge ogni cinque secondi — cioè la scheda aperta qui si accorge da
 * sé se qualcuno in baia carica un'altra cassetta.
 *
 * Non ci sono la foto e le etichette dei gruppi, e non è una dimenticanza:
 * quelle stanno sul **foglio** scritto al banco, che è un'altra pagina
 * (`crate-detail.tsx`) e un altro archivio. Il flusso conosce la cassetta come
 * unità di lavoro — di chi è, cosa c'è dentro, dove deve passare — e questa
 * pagina dice quello.
 */

import { sortSizes } from '@errebi/domain';

import { Sezione } from '../components/section';
import { Stato } from '../components/state';
import { Spazio, Testata, Voce } from '../components/header';
import { useFlusso } from '../flow-api';

interface Cassetta {
  codice: string;
  ordine: string;
  cliente: string;
  sku: string;
  colore: string;
  capi: number;
  /**
   * La scala delle taglie. Assente = consegnata da un banco più vecchio: lo
   * stato del flusso è un JSONB che nessuna migrazione riallinea, e il campo è
   * facoltativo nel dominio per la stessa ragione.
   */
  taglie?: Record<string, number>;
  /** I reparti da attraversare. Vuoto = neutro: salta la produzione. */
  pers: string[];
  /** Quelli già chiusi: `pers` non cala, la lavorazione si aggiunge qui. */
  chiuse: string[];
}
interface VistaScarico {
  /** Every trolley the office has, plus any taken off that still carries crates. */
  carrelli: Record<string, Cassetta[]>;
  posti: number;
}

/** Accordo al singolare. Scritto una volta: «1 cassette» si nota, e male. */
function quante(n: number, uno: string, molti: string): string {
  return `${n} ${n === 1 ? uno : molti}`;
}

export function DettaglioCarrello({ carrello }: { readonly carrello: string }) {
  const { dati, caricando } = useFlusso<VistaScarico>('scarico', '/scarico');

  // The trolley list is office data and arrives with the view: existence can
  // only be judged once the view is here.
  if (caricando || !dati) return <Stato quale="caricando" dice="Guardo cosa c'è sopra…" />;

  /*
   * «Non esiste» prima di «vuoto», e sono due risposte diverse a due domande
   * diverse. K9 non c'è: chi ha digitato quell'indirizzo ha sbagliato, e
   * tornare a guardarlo non cambierà niente. K4 c'è ed è vuoto: fra dieci
   * minuti magari no.
   */
  if (!(carrello in dati.carrelli))
    return (
      <Stato
        quale="sconosciuto"
        dice={`Il carrello ${carrello} non esiste. Sono ${Object.keys(dati.carrelli).join(', ')}.`}
      />
    );

  const cassette = dati.carrelli[carrello] ?? [];
  const capi = cassette.reduce((n, c) => n + c.capi, 0);
  const liberi = Math.max(0, dati.posti - cassette.length);

  return (
    <div className="shell">
      <Testata>
        <Voce k="Carrello" v={<span className="carrello__nome">{carrello}</span>} />
        <Voce k="Cassette" v={`${cassette.length} su ${dati.posti}`} />
        <Voce k="Capi" v={capi} />
        <Spazio />
        {/*
          La via del ritorno, scritta.

          La pagina si apre in una scheda a parte e chi ci arriva non ha sempre
          il menu del banco sotto gli occhi: senza questo l'unica uscita è
          chiudere la scheda, che è un gesto che si impara e non si vede.
        */}
        <a className="carrello__indietro" href="/unloading">
          ← Torna allo scarico
        </a>
      </Testata>

      <div className="carrello">
        <Sezione
          titolo={`${carrello} · ${quante(cassette.length, 'cassetta', 'cassette')} su ${dati.posti}`}
          nota={
            cassette.length === 0
              ? undefined
              : `${quante(capi, 'capo', 'capi')} in tutto · ${
                  liberi === 0
                    ? 'il carrello è pieno'
                    : quante(liberi, 'posto libero', 'posti liberi')
                }`
          }
        >
          {cassette.length === 0 ? (
            <p className="booth__vuoto">
              Il carrello {carrello} è vuoto. Le cassette ci salgono dalla baia, nella schermata di
              scarico.
            </p>
          ) : (
            <div className="carrello__posti">
              {cassette.map((c) => (
                <Scheda key={c.codice} c={c} />
              ))}
              {/*
                Un riquadro per ogni posto rimasto, e non solo una frase che
                dice quanti sono: davanti al carrello si conta guardando. Il
                numero resta comunque scritto in testata, per chi lo vuole letto.
              */}
              {Array.from({ length: liberi }, (_, i) => (
                <Libero key={i} />
              ))}
            </div>
          )}
        </Sezione>
      </div>
    </div>
  );
}

/**
 * Una cassetta: le due cose che si leggono da lontano a sinistra, il resto a
 * destra.
 *
 * La fascia sul bordo sinistro è quella delle tessere del banco e dice la
 * stessa cosa: verde se non resta niente da fare, ambra se una tappa manca,
 * neutra se il capo la produzione non la attraversa affatto.
 */
function Scheda({ c }: { readonly c: Cassetta }) {
  const restano = c.pers.filter((r) => !c.chiuse.includes(r));
  const stato = c.pers.length === 0 ? 'neutra' : restano.length === 0 ? 'chiusa' : 'aperta';

  return (
    // Il nome accessibile è il codice: quattro tessere senza nome, lette una
    // dopo l'altra, sono quattro blocchi di testo senza confini.
    <article
      className={`carrello__cassetta carrello__cassetta--${stato}`}
      aria-label={`Cassetta ${c.codice}`}
    >
      <div className="carrello__conto">
        <span className="carrello__cod">{c.codice}</span>
        <span className="carrello__capi">{c.capi}</span>
        <span className="carrello__capi-k">{c.capi === 1 ? 'capo' : 'capi'}</span>
      </div>

      <div className="carrello__testo">
        <span className="carrello__cliente">{c.cliente}</span>
        <span className="carrello__ordine">
          ordine {c.ordine} · {c.sku}
        </span>
        <span className="carrello__articolo">{c.colore}</span>

        <Taglie codice={c.codice} taglie={c.taglie} />
        <Reparti pers={c.pers} chiuse={c.chiuse} restano={restano} />
      </div>
    </article>
  );
}

/** Un posto ancora libero. Vuoto si vede; «uno su quattro» si calcola. */
function Libero() {
  return (
    <div className="carrello__libero">
      <span className="carrello__libero-k">Posto libero</span>
      <span className="carrello__libero-n">
        Ci sta ancora una cassetta. Caricala dalla schermata di scarico.
      </span>
    </div>
  );
}

/**
 * La scala delle taglie: quello per cui la pagina esiste.
 *
 * «24 capi» non si controlla stando davanti al carrello — «S ×4, M ×8, L ×8,
 * XL ×4» sì, e senza aprire la cassetta. L'ordine è quello della scala e lo
 * decide `sortSizes` del dominio, lo stesso che ordina le taglie sul foglio del
 * banco: due ordini diversi per lo stesso dato in due pagine sono un modo per
 * far sembrare sbagliata quella giusta.
 */
function Taglie({
  codice,
  taglie,
}: {
  readonly codice: string;
  readonly taglie?: Record<string, number> | undefined;
}) {
  // Assente non è vuoto, e non va disegnato vuoto: una scala senza pastiglie si
  // legge come «cassetta vuota», che è un'altra cosa e allarma per niente.
  if (!taglie)
    return (
      <p className="carrello__senza-taglie">
        <strong>Taglie non registrate.</strong> Consegnata prima che il banco le allegasse: il
        contenuto sta sul foglio attaccato alla cassetta.
      </p>
    );

  return (
    <ul className="carrello__taglie" title={`Taglie di ${codice}`}>
      {sortSizes(Object.keys(taglie))
        .filter((t) => (taglie[t] ?? 0) > 0)
        .map((t) => (
          <li key={t}>
            <span className="carrello__taglia">{t}</span>
            <span className="carrello__quante">×{taglie[t]}</span>
          </li>
        ))}
    </ul>
  );
}

/**
 * Cosa resta da fare su questa cassetta, e cosa è già fatto.
 *
 * Le due liste stanno separate perché la domanda di chi guarda un carrello
 * pronto a partire è una sola: **ha ancora una tappa?** Un elenco unico di
 * reparti con uno spuntato costringe a leggerlo tutto per rispondere. I
 * reparti restano scritti col loro codice — `DTF`, `WEMB` — come in tutte le
 * altre schermate: tradurli qui e non altrove farebbe parlare a questa pagina
 * una lingua che il resto del banco non parla.
 */
function Reparti({
  pers,
  chiuse,
  restano,
}: {
  readonly pers: string[];
  readonly chiuse: string[];
  readonly restano: string[];
}) {
  if (pers.length === 0)
    return (
      <p className="carrello__reparti">
        <span className="carrello__bollo carrello__bollo--neutro">NEUTRO</span>
        <span className="carrello__reparti-k">non passa dalla produzione</span>
      </p>
    );

  return (
    <p className="carrello__reparti">
      <span className="carrello__reparti-k">da fare</span>
      {restano.length === 0 ? (
        <span className="carrello__bollo carrello__bollo--fatto">niente</span>
      ) : (
        restano.map((r) => (
          <span key={r} className="carrello__bollo carrello__bollo--resta">
            {r}
          </span>
        ))
      )}
      {chiuse.length > 0 && (
        <>
          <span className="carrello__reparti-k">fatto</span>
          {chiuse.map((r) => (
            <span key={r} className="carrello__bollo carrello__bollo--fatto">
              {r}
            </span>
          ))}
        </>
      )}
    </p>
  );
}
